import React from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { usd, num } from "@/lib/format";
import { AlertTriangle, Inbox } from "lucide-react";

export const METRICS = {
  gross_sales: { label: "Gross Sales", color: "#059669", axis: "money" },
  net_revenue: { label: "Net Revenue", color: "#2563EB", axis: "money" },
  contribution_profit: { label: "Contribution Profit", color: "#8B5CF6", axis: "money" },
  ppc: { label: "PPC Spend", color: "#F97316", axis: "money" },
  units: { label: "Units", color: "#06B6D4", axis: "units" },
};

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-0 border border-edge-subtle rounded-ctrl shadow-menu px-3 py-2 text-compact">
      <div className="text-micro font-mono text-ink-muted mb-1">{label}</div>
      {payload.map((p) => {
        const key = p.dataKey.replace("__prev", "");
        const m = METRICS[key];
        return (
          <div key={p.dataKey} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: p.color }} />{m?.label}{p.dataKey.includes("__prev") ? " (prev)" : ""}</span>
            <span className="font-mono tabnum">{m?.axis === "units" ? num(p.value) : usd(p.value)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function PerformanceChart({ data, visibleMetrics, compare, state = "ready", height = 260 }) {
  if (state === "loading") return <div className="ez-shimmer rounded-panel" style={{ height }} data-testid="chart-skeleton" />;
  if (state === "error") return <div style={{ height }} className="grid place-items-center text-compact text-red-600 border border-red-600/20 rounded-panel bg-red-100/30"><div className="text-center"><AlertTriangle className="h-6 w-6 mx-auto mb-2" />Couldn't load performance data. <button onClick={() => window.location.reload()} className="text-signal-blue underline ml-1">Retry</button></div></div>;
  if (!data?.length || state === "empty") return <div style={{ height }} className="grid place-items-center text-compact text-ink-muted border border-edge-subtle rounded-panel" data-testid="chart-empty"><div className="text-center"><Inbox className="h-6 w-6 mx-auto mb-2" />No data in this range.</div></div>;

  const showUnits = visibleMetrics.includes("units");
  const showMoney = visibleMetrics.some((m) => METRICS[m]?.axis === "money");
  return (
    <div style={{ width: "100%", height }} data-testid="performance-chart">
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E9F0" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#78859A" }} tickLine={false} axisLine={{ stroke: "#E4E9F0" }} minTickGap={24} />
          {showMoney && <YAxis yAxisId="money" tick={{ fontSize: 11, fill: "#78859A" }} tickLine={false} axisLine={false} width={54} tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />}
          {showUnits && <YAxis yAxisId="units" orientation="right" tick={{ fontSize: 11, fill: "#78859A" }} tickLine={false} axisLine={false} width={44} />}
          <Tooltip content={<Tip />} />
          {visibleMetrics.map((m) => (
            <Line key={m} yAxisId={METRICS[m].axis} type="monotone" dataKey={m} stroke={METRICS[m].color} strokeWidth={2} dot={data.length <= 1} activeDot={{ r: 4 }} isAnimationActive={false} />
          ))}
          {compare && visibleMetrics.map((m) => (
            <Line key={m + "p"} yAxisId={METRICS[m].axis} type="monotone" dataKey={m + "__prev"} stroke={METRICS[m].color} strokeWidth={1.5} strokeDasharray="4 4" dot={false} isAnimationActive={false} opacity={0.5} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
